import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { newCompany, deleteCompanyAction, companiesList } from '../../../api/redux/slices/SuperAdmin';
import { icons } from '../../../assets/icons/index';
import AddCompany from './AddCompany';
import './index.scss';
import UpdateCompany from './UpdateCompany';
import { CSVLink } from 'react-csv';
import { useParams, useNavigate } from 'react-router-dom';
import { LoaderComponent } from '../../../helpers/components/loader';
import Papa from 'papaparse';
import { DataErrorModal, showToast } from '../../../components/modal';
import Pagination from '../../../helpers/components/Pagination';
import DeleteConfirmationModal from '../../admin/addUser/ShopDeleteModal';
import useStableArr from '../../../utils/useStableArr';
import { handleCheck, handleCheckAll } from '../../../utils/checkUtilsSuperAdmin';

const CompanyPage = () => {
    const { listCompanies, checkedList, total } = useSelector((state) => state.superAdmin);
    const dispatch = useDispatch();
    const params = useParams();
    const navigate = useNavigate();

    const [modalOpen, setModalOpen] = useState(false);
    const [page, setPage] = useState(Number(params.page));
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [delModalOpen, setDelModalOpen] = useState(false);
    const [lineIndice, setLineIndice] = useState(null);
    const [typing, setTyping] = useState(false);
    const [newArr, setNewArr] = useState(listCompanies.map((el) => ({ ...el, checked: false })));
    const [isCheck, setIsCheck] = useState(false);
    const [companyName, setCompanyName] = useState('');
    const token = localStorage.getItem('accesstoken');
    const [errorData, setErrorData] = useState([]);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isloading, setIsLoading] = useState(false);

    const fileInputRef = useRef(null);
    const getfile = () => {
        fileInputRef.current.value = null;
        fileInputRef.current.click();
    };

    const headers = [
        { label: 'NOM', key: 'name' },
        { label: 'ADRESSE', key: 'address' },
        { label: 'TELEPHONE', key: 'phone' },
        { label: 'FAX', key: 'fax' },
        { label: 'EMAIL', key: 'email' },
    ];

    const csvCompaniesReport = {
        filename: 'Sociétés.csv',
        data: checkedList,
        headers: headers,
    };

    const stableDispatch = useCallback(() => {
        setIsLoading(true);
        dispatch(
            companiesList({ token: token, search: companyName, page: companyName ? undefined : page }),
        )
            .catch(() => showToast("Quelque chose s'est mal passé !", 'error'))
            .finally(() => setIsLoading(false));
    }, [dispatch, page, companyName, token]);

    useEffect(() => {
        if (token) {
            stableDispatch();
        }
    }, [stableDispatch, addModalOpen, modalOpen, token, navigate]);

    useStableArr(listCompanies, checkedList, setNewArr, setIsCheck);

    const handleDeleteSelected = () => {
        const companiesToDelete = newArr.filter((company) => company.checked);
        if (companiesToDelete.length === 0) return;

        Promise.all(
            companiesToDelete.map((company) =>
                dispatch(deleteCompanyAction({ token: token, id: company._id })),
            ),
        ).then(() => {
            stableDispatch();
            showToast('Sociétés supprimées', 'info');
        });
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            const reader = new FileReader();
            reader.onload = async ({ target }) => {
                const csv = Papa.parse(target.result, { header: true });
                const parsedData = csv?.data;
                const errors = [];

                const promises = parsedData.reduce((accumulator, values) => {
                    const data = {
                        name: values.NOM,
                        address: values.ADRESSE,
                        phone: values.TELEPHONE,
                        fax: values.FAX,
                        email: values.EMAIL,
                    };

                    if (data.name) {
                        accumulator.push(
                            dispatch(newCompany({ token: token, data }))
                                .unwrap()
                                .catch((err) => {
                                    errors.push({
                                        Nom: data.name,
                                        error: err?.error || err?.message,
                                    });
                                }),
                        );
                    }
                    return accumulator;
                }, []);

                await Promise.all(promises);

                if (errors.length > 0) {
                    setErrorData(errors);
                    setErrorModalOpen(true);
                } else showToast('Sociétés ajoutées', 'success');

                stableDispatch();
            };
            reader.readAsText(selectedFile);
        }
    };

    const handleDelete = () => {
        dispatch(deleteCompanyAction({ token: token, id: lineIndice }))
            .unwrap()
            .then(() => {
                showToast('Société supprimée', 'info');
                stableDispatch();
            });
    };

    return (
        <div id='companyPage'>
            {showDeleteModal && (
                <DeleteConfirmationModal
                    onDelete={() => {
                        handleDeleteSelected();
                        setShowDeleteModal(false);
                    }}
                    onCancel={() => setShowDeleteModal(false)}
                    msg={
                        checkedList.length > 1
                            ? 'Êtes-vous sûr de vouloir supprimer toutes les sociétés sélectionnées ?'
                            : 'Êtes-vous sûr de vouloir supprimer cette société ?'
                    }
                />
            )}
            <div className='navbar'>
                <div className='left'>
                    <div className='nav-search'>
                        <input
                            type='text'
                            placeholder='Rechercher ...'
                            value={companyName}
                            onChange={(e) => {
                                setCompanyName(e.target.value);
                                setTyping(true);
                            }}
                        />
                        <icons.FiSearch className='searchicon' />
                        {typing && (
                            <icons.GrFormClose
                                className='searchicon'
                                onClick={() => {
                                    setCompanyName('');
                                    setTyping(false);
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className='right'>
                    <div className='right-items'>
                        <button className='button-primary' onClick={() => setAddModalOpen(!addModalOpen)}>
                            <icons.SiAddthis />
                            <span>Société</span>
                        </button>
                        <CSVLink {...csvCompaniesReport} target='blank'>
                            <button className='button-nature' title='exporter sociétés'>
                                <icons.BiExport />
                            </button>
                        </CSVLink>
                        <button className='button-nature' title='importer sociétés' onClick={getfile}>
                            <icons.BiImport />
                            <input
                                type='file'
                                id='fileuploading'
                                ref={fileInputRef}
                                style={{ display: 'none' }}
                                accept='.csv'
                                onChange={handleFileChange}
                            />
                        </button>
                    </div>
                </div>
            </div>
            <div id='head'>
                <div className='title-user'>
                    <div>
                        <h1>Sociétés</h1>
                    </div>
                    <div
                        className='deluser'
                        onClick={() => {
                            if (checkedList.length > 0) {
                                setShowDeleteModal(true);
                            } else showToast('Choisissez au moins une société.', 'warn');
                        }}
                    >
                        <icons.FaTrashAlt className='del-company' />
                    </div>
                </div>
            </div>
            <AddCompany addModalOpen={addModalOpen} setAddModalOpen={setAddModalOpen} />
            {modalOpen && (
                <UpdateCompany modalOpen={modalOpen} setModalOpen={setModalOpen} lineIndice={lineIndice} />
            )}
            {delModalOpen && (
                <DeleteConfirmationModal
                    onDelete={() => {
                        handleDelete();
                        setDelModalOpen(false);
                    }}
                    onCancel={() => setDelModalOpen(false)}
                    msg={'Êtes-vous sûr de vouloir supprimer cette société ?'}
                />
            )}
            <div id='companiesContent'>
                <table id='companies'>
                    <thead>
                        <tr id='titres'>
                            <td className='input-check'>
                                <input
                                    type='checkbox'
                                    className='checkbox'
                                    checked={isCheck}
                                    onChange={() => handleCheckAll(dispatch, newArr, setNewArr, isCheck, setIsCheck)}
                                />
                            </td>
                            <td>NOM</td>
                            <td>ADRESSE</td>
                            <td>TÉLÉPHONE</td>
                            <td>EMAIL</td>
                            <td>Sponsor_code</td>
                            <td>ACTION</td>
                        </tr>
                    </thead>
                    <tbody>
                        {isloading ? (
                            <tr>
                                <td colSpan='7'>
                                    <LoaderComponent margin='10%' />
                                </td>
                            </tr>
                        ) : newArr?.length > 0 ? (
                            newArr.map((company, index) => (
                                <tr id={`element-${index}`} key={company._id} className='company-row'>
                                    <td className='input-check'>
                                        <input
                                            type='checkbox'
                                            checked={company.checked}
                                            className='checkbox'
                                            onChange={() => handleCheck(dispatch, newArr, setNewArr, company._id)}
                                        />
                                    </td>
                                    <td>{company.name}</td>
                                    <td>{company.address}</td>
                                    <td>{company.phone}</td>
                                    <td>{company.email}</td>
                                    <td>{company.sponsorship_code}</td>
                                    <td className='last-company-element'>
                                        <icons.FaRegEdit
                                            className='edit-company'
                                            onClick={() => {
                                                setModalOpen(true);
                                                setLineIndice(company._id);
                                            }}
                                        />
                                        <icons.FaTrashAlt
                                            className='del-company'
                                            onClick={() => {
                                                setDelModalOpen(true);
                                                setLineIndice(company._id);
                                            }}
                                        />
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan='7' style={{ textAlign: 'center', padding: '50px' }}>
                                    Aucune société trouvée
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <Pagination page={page} setPage={setPage} pageLink={`/superadmin/companies/`} total={total} />
            <DataErrorModal
                isOpen={errorModalOpen}
                closeModal={() => setErrorModalOpen(false)}
                errorData={errorData}
                width='35rem'
                height='auto'
            />
        </div>
    );
};

export default CompanyPage;